import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { debounce } from 'es-toolkit';

import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/web-base';

import { useFormMode } from '../contexts/FormModeContext';
import { postingConstraints } from '../utils/formValidation';

import { DEFAULT_ERROR_MESSAGE } from '../constants/ErrorMessageConstants';
import TimeConstants from '../constants/TimeConstants';

import { useWizardNavigation } from '../components/CreateListingPage/WizardNavigationProvider';

import useForm from './useForm';
import { MultiEditSoftError } from './useMultiEditMutation';

import type { UseMutationResult } from '@zg-rentals/react-query5';

/**
 * Extract the TVariables type from a UseMutationResult.
 *
 * This utility type enables automatic type inference for useFieldSave,
 * ensuring buildMutationVariables returns the exact shape expected by the mutation.
 */
type ExtractMutationVariables<TMutation> =
  TMutation extends UseMutationResult<infer _TData, infer _TError, infer TVariables, infer _TContext>
    ? TVariables
    : unknown;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UseFieldSaveOptions<T, TMutation extends UseMutationResult<any, any, any, any>> = {
  fieldName: string;
  initialValue: T;
  mutation: TMutation;
  /** Return null to skip the save, e.g. when an identifier the request needs has not loaded yet. */
  buildMutationVariables: (value: T) => ExtractMutationVariables<TMutation> | null;

  // For Radio/Checkbox/Select inputs that don't fire blur events
  autoSaveOnChange?: boolean;

  // Set to 0 to save on change with no debounce, for callers that already debounce downstream
  // (e.g. a shared batch window). Defaults to TimeConstants.SELECTION_DEBOUNCE_TIME_MS.
  autoSaveDebounceMs?: number;

  // Only needed when the API error fieldName differs from the form's fieldName
  // (e.g. form uses "description" but API returns errors with fieldName "fullDescription")
  apiFieldName?: string;

  // When false, validation errors are not reported to the wizard navigation context.
  // Use for conditionally-visible fields that should only block Next when shown.
  enabled?: boolean;

  // Validation the field owns rather than postingConstraints, for checks that cannot run
  // synchronously. Blocks the save and Next exactly like a built-in validation error.
  externalError?: string | null;
};

export type UseFieldSaveReturn<T> = {
  value: T;
  onChange: (value: T | null | undefined) => void;
  /** Fire-and-forget. Never rejects; failures surface through `error`. */
  onBlur?: () => void;
  /** Saves and rejects on failure, like react-query's mutateAsync. Use when the caller branches on the error. */
  saveAsync: () => Promise<void>;
  error: string | null | undefined;
  isPending: boolean;
};

/**
 * Hook that encapsulates field save logic based on form mode.
 *
 * In wizard mode:
 * - Returns onBlur handler that saves automatically
 *
 * In modal mode:
 * - onBlur is undefined (no auto-save)
 * - Registers save function with FormModeContext
 * - Parent modal triggers save via context.triggerAllSaves()
 *
 * Uses existing useForm hook for validation to maintain compatibility
 * with legacy postingConstraints.
 *
 * @example
 * ```tsx
 * const updateMutation = usePropertyEditMutation({
 *   mutationHook: useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBedrooms,
 * });
 *
 * const { value, onChange, onBlur, error } = useFieldSave({
 *   fieldName: 'bedrooms',
 *   initialValue: 2,
 *   mutation: updateMutation,
 *   buildMutationVariables: (val) => ({
 *     postData: { floorPlanId: id, numBedrooms: val },
 *   }),
 * });
 *
 * <Select value={value} onChange={onChange} onBlur={onBlur} error={error} />
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFieldSave = <T, TMutation extends UseMutationResult<any, any, any, any>>({
  fieldName,
  initialValue,
  mutation,
  buildMutationVariables,
  autoSaveOnChange = false,
  autoSaveDebounceMs = TimeConstants.SELECTION_DEBOUNCE_TIME_MS,
  apiFieldName,
  enabled = true,
  externalError,
}: UseFieldSaveOptions<T, TMutation>): UseFieldSaveReturn<T> => {
  const formModeContext = useFormMode();
  const navigation = formModeContext.mode === 'wizard' ? useWizardNavigation() : null;
  const fieldId = useId();
  const { enqueueToast } = useToast();

  const { inputs, handleChange, hasFieldError, errorObj } = useForm({ [fieldName]: initialValue }, postingConstraints);

  const value = inputs[fieldName] as T;

  const validationError = (errorObj as Record<string, string | undefined>)[fieldName] ?? externalError ?? undefined;
  const effectiveApiFieldName = apiFieldName ?? fieldName;
  const mutationError = mutation.error
    ? MultiEditSoftError.is(mutation.error)
      ? mutation.error.getMessageForField(effectiveApiFieldName)
      : (mutation.error as DatarouterError).errorDetails
        ? DatarouterError.getErrorDetails(mutation.error).errors.find((error) => error.name === fieldName)?.message
        : DatarouterError.getDisplayMessage(mutation.error, DEFAULT_ERROR_MESSAGE)
    : null;

  // Centralized logic for building variables with skip checks
  const buildVariablesOrNull = useCallback(
    (val: T | null | undefined) => {
      if (externalError || hasFieldError(fieldName, val as T) || val === initialValue) {
        return null;
      }

      const variables = buildMutationVariables(val as T);

      // The caller has no identifier to save against. Sending the request anyway is a 500, and
      // dropping it without a word looks like a successful save, so tell the user and Dragnet.
      if (variables === null) {
        enqueueToast(<Toast tone="critical">Your change couldn’t be saved. Try again.</Toast>);
        logError({
          error: new Error(`Save skipped, no identifier for ${fieldName}`),
          errorType: 'FieldSaveSkipped',
          context: { fieldName },
        });
      }

      return variables;
    },
    [fieldName, initialValue, hasFieldError, buildMutationVariables, externalError, enqueueToast],
  );

  // Stable debounced save using refs to avoid recreation
  const latestRefs = useRef({ buildVariablesOrNull, mutation, value });
  latestRefs.current = { buildVariablesOrNull, mutation, value };

  // The debounce wait is fixed when the timer is created, so it is read once per mount.
  const debounceMsRef = useRef(autoSaveDebounceMs);

  const executeSave = useCallback(async () => {
    const { buildVariablesOrNull: bvon, mutation: mut, value: val } = latestRefs.current;
    const variables = bvon(val);
    if (!variables) {
      return;
    }

    await mut.mutateAsync(variables);
  }, []);

  // onBlur is handed to the DOM and to callers that discard the promise, so a rejection there
  // has no listener and would surface as unhandled. Failures still render from `error`.
  const handleBlur = useCallback(() => {
    void executeSave().catch(() => undefined);
  }, [executeSave]);

  const executeSaveDebounced = useMemo(
    () =>
      debounce((newValue: T | null | undefined) => {
        const variables = latestRefs.current.buildVariablesOrNull(newValue);
        if (variables) {
          latestRefs.current.mutation.mutateAsync(variables).catch(() => undefined);
        }
      }, debounceMsRef.current),
    [],
  );

  // Report validation errors to navigation context in wizard mode.
  // Gated on `enabled` so conditionally-visible fields don't block Next when hidden.
  useEffect(() => {
    if (validationError && enabled) {
      navigation?.reportError();
      return () => navigation?.clearError();
    }
  }, [validationError, navigation, formModeContext.mode, enabled]);

  // In modal mode, register mutation + variables builder
  useEffect(() => {
    if (formModeContext.mode === 'modal') {
      formModeContext.registerSave(fieldId, {
        mutation,
        buildVariables: () => buildVariablesOrNull(value),
        hasError: () => !!validationError,
      });
      return () => formModeContext.unregisterSave(fieldId);
    }
  }, [formModeContext, fieldId, mutation, value, buildVariablesOrNull, validationError]);

  const onChange = (newValue: T | null | undefined) => {
    latestRefs.current.value = newValue as T;
    handleChange({ target: { name: fieldName, value: newValue as T } });

    // Auto-save for inputs without blur events (wizard mode only)
    if (autoSaveOnChange && formModeContext.mode === 'wizard') {
      if (debounceMsRef.current === 0) {
        void executeSave();
      } else {
        executeSaveDebounced(newValue);
      }
    }
  };

  const validateField = useCallback(() => {
    hasFieldError(fieldName, value);
  }, [fieldName, value, hasFieldError]);

  if (formModeContext.mode === 'wizard') {
    return {
      value,
      onChange,
      onBlur: autoSaveOnChange ? undefined : handleBlur, // No onBlur for auto-save inputs
      saveAsync: executeSave,
      error: validationError || mutationError,
      isPending: mutation.isPending,
    };
  }

  // Modal mode
  return {
    value,
    onChange,
    onBlur: validateField, // Validate on blur, save deferred until modal Save button
    saveAsync: executeSave,
    error: validationError || mutationError,
    isPending: mutation.isPending,
  };
};
